import { supertypeClass, remote, property, amorphicStatic } from '@haventech/amorphic';

import { deferEmailChange, log, queryFilter, validateEmail, passwordExpiresMinutes } from '../AuthenticationHelpers';
import * as AuthenticationHelpers from '../AuthenticationHelpers';
import { ActivatedRoute } from '@angular/router';
import jwt from 'jsonwebtoken';

import * as urlparser from 'url';

import { Account } from '../account/Account';
import { Auth0Client, Auth0Response, Auth0Error, Auth0ResponseAccount, Auth0UserMetadata } from './Auth0Client';
import { Auth0Configs, Auth0Utils } from './Auth0Utils';
import { AuthenticatingController, PreRegistrationVerificationResult } from '../AuthenticatingController';
import { Auth0AuthorizeResponse } from '../../../coverpath-common/angular/services/authentication/auth0.service';
import { AuthOwner, Customer } from '../customer/Customer';
import {
  Identity,
  IdentityProviders,
  IdentityProvidersDisplayName,
  IdentitySource,
} from '../account/identity/Identity';
import { IdentityService } from '../account/identity/IdentityService';
import { PrincipalCustomer } from '../PrincipalCustomer';
import { ManagedEntity } from '../customer/ManagedEntity';
import { AuthenticatedPrincipal } from '../AuthenticatedPrincipal';
import { CustomerUtils } from '../customer/CustomerUtils';
import { Application } from '../customer/Application';
import { isCode, removePrefixForCode } from './Auth0CodeUtils';
import { LoggerSingleton, Category, PiiLevelEnum } from '../logger/LoggerSingleton';
import { UICategory, UILoggerSingleton, UIPiiLevelEnum } from '../logger/UILoggerSingleton';

export const ALPHANUMERIC_REGEX = /([^0-9a-zA-Z])/g;
@supertypeClass
export abstract class Auth0Controller extends AuthenticatingController {
  @property({ toClient: true, toServer: false, sensitiveData: true })
  auth0clientId: string;

  @property({ toClient: true, toServer: false, sensitiveData: true })
  auth0CustomDomain: string;

  @property({ toClient: true, toServer: false, sensitiveData: true })
  auth0domain: string;

  @property({ toClient: true, toServer: false, sensitiveData: true })
  auth0audience: string;

  @property({ toClient: true, toServer: false, sensitiveData: true })
  auth0responseType: string;

  @property({ toClient: false, toServer: true, sensitiveData: true })
  auth0redirectUri: string;

  @property({ toClient: true, toServer: false, sensitiveData: true })
  auth0scope: string;

  @property({ toClient: true, toServer: false, sensitiveData: true })
  auth0connection: string;

  @property({ toClient: false, toServer: false, sensitiveData: true })
  auth0userId: string;

  @property({ toClient: false, toServer: false, sensitiveData: true })
  auth0IdToken: string;

  @property({ toClient: false, toServer: false, sensitiveData: true })
  auth0AccessToken: string;

  @property({ toClient: false, toServer: false, sensitiveData: true })
  managedEntityId: string;

  @property({ toClient: true, toServer: true })
  isZenDeskLogin: boolean = false;

  @property({ toClient: true, toServer: false })
  authenticatedIdentity: Identity;

  @property({ toClient: true, toServer: false, sensitiveData: true })
  customerAccount: Account;

  @property({ toClient: true, toServer: false, sensitiveData: true })
  primaryIdentity: Identity;

  @property({ toClient: true, toServer: false, sensitiveData: true })
  auth0errorMessage: Auth0Error;

  @property({ toClient: true, toServer: false, sensitiveData: true })
  auth0mode: string;

  @property({ toClient: true })
  hasAMCidentity: boolean = false;

  @property({ toClient: true, toServer: false, sensitiveData: true })
  auth0ErrorFromProvider: string;

  @property({ toClient: true, toServer: false, sensitiveData: true })
  activeClientId: string;

  identityService: IdentityService;

  @property({ toClient: true, toServer: true })
  convertCustomer: boolean;

  @property({ toClient: true, toServer: false, sensitiveData: true })
  auth0provider;

  @property({ toClient: true, toServer: false, sensitiveData: true })
  signUpOrigin: string;

  public serverInit() {
    super.serverInit();
  }

  /**
   *
   * @param auth0Result - Payload from Auth0
   * @param fragmentOrCode - From Auth0 - Either Fragment that has a code, token etc. or just Code
   */
  @remote({ on: 'server' })
  public async establishSession(
    auth0Result: Auth0AuthorizeResponse,
    fragmentOrCode: string
  ): Promise<AuthenticationInformation> {
    await this.processAuth0Code(auth0Result, fragmentOrCode);
    const authInformation = await this.publicControllerAuthenticate();

    return authInformation;
  }

  @remote({ on: 'server' })
  public async prepareSessionData(authenticationInformation: AuthenticationInformation): Promise<void> {
    await this.postAuthExecution(this.getPrincipal(), authenticationInformation.isExistingUser);
    LoggerSingleton.getInstance().info(
      {
        module: 'Auth0Controller',
        function: 'prepareSessionData',
        category: Category.security,
        context: {
          piiLevel: PiiLevelEnum.s2,
        },
        data: {
          email: this.email,
        },
      },
      'Completing preparation of session data'
    );
  }

  /**
   *
   * @param auth0Result
   * @param fragmentOrCode - From Auth0 - Either Fragment that has a code, token etc. or just Code
   */
  @remote({ on: 'server' })
  async processAuth0Code(auth0Result: Auth0AuthorizeResponse, fragmentOrCode: string): Promise<void> {
    let code: string;
    if (isCode(fragmentOrCode)) {
      code = removePrefixForCode(fragmentOrCode);
    } else {
      code = Auth0Utils.parseAuthenticationFragment(fragmentOrCode).code;
    }

    const token: Auth0Response = await this.exchangeAuth0CodeForToken(code, this.auth0redirectUri);
    this.setAuth0Properties(token);

    LoggerSingleton.getInstance().info(
      {
        module: 'Auth0Controller',
        function: 'processAuth0Code',
        category: Category.security,
        context: { piiLevel: PiiLevelEnum.s2 },
        data: {
          email: this.email,
          managedEntityId: this.managedEntityId || '',
        },
      },
      'Processed Auth0 code'
    );
  }

  protected setAuth0Properties(response: Auth0Response): void {
    //Auth0Response toggles between providing the jwt token in id_token and access_token
    let tokenPayLoad: jwt.JwtPayload;
    if (response.id_token) {
      tokenPayLoad = this.decodeJwtToken(response.id_token);
      this.auth0IdToken = response.id_token;
    } else {
      tokenPayLoad = this.decodeJwtToken(response.access_token);
      this.auth0IdToken = response.access_token;
    }

    if (response.access_token) {
      this.auth0AccessToken = response.access_token;
    }

    this.auth0userId = tokenPayLoad.sub;
    this.activeClientId = Array.isArray(tokenPayLoad.aud) ? tokenPayLoad.aud[0] : (tokenPayLoad.aud as string);
    this.email = tokenPayLoad.email;
    this.isAccountVerified = tokenPayLoad.email_verified;
    this.auth0provider = this.getIdentityService().getProviderFromIdentifier(tokenPayLoad.sub);

    LoggerSingleton.getInstance().info(
      {
        module: 'Auth0Controller',
        function: 'setAuth0Properties',
        category: Category.milestone,
        context: { piiLevel: PiiLevelEnum.s2 },
        data: {
          email: this.email,
          isAccountVerified: this.isAccountVerified,
        },
      },
      'Set Auth0 Properties'
    );
  }

  @remote({ on: 'server' })
  async publicControllerAuthenticate(
    policyNumber?: string | null,
    applicationNumber?: string | null
  ): Promise<AuthenticationInformation> {
    LoggerSingleton.getInstance().info(
      {
        module: 'Auth0Controller',
        function: 'publicControllerAuthenticate',
        category: Category.milestone,
        context: { piiLevel: PiiLevelEnum.s2 },
        data: {
          email: this.email,
        },
      },
      'Beginning publicControllerLogin'
    );

    await this.preAuthExecution();

    const identifiedPrincipal: IdentifiedPrincipal = await this.identifyPrincipal(
      this.email,
      this.firstName,
      this.lastName,
      policyNumber,
      applicationNumber
    );

    await this.postGetPrincipal();

    await this.authenticatePrincipal(identifiedPrincipal.principal, identifiedPrincipal.isExistingUser, this.password);

    return this.returnInformationToClient(identifiedPrincipal);
  }

  protected async preRegistrationCheck(): Promise<PreRegistrationVerificationResult> {
    let result: PreRegistrationVerificationResult = null;

    const isAuth0Enabled = await this.getIsAuth0Enabled();
    UILoggerSingleton.getInstance().info(
      {
        module: 'Auth0Controller',
        function: 'preRegistrationCheck',
        category: UICategory.security,
        data: {
          isAuth0Enabled: isAuth0Enabled,
        },
      },
      `preRegistrationCheck`
    );

    if (isAuth0Enabled) {
      const password = this.password || this.newPassword;

      try {
        await Auth0Client.createAccount(this.email, password);
      } catch (error) {
        if (error && error.message) {
          if (error.error && error.error.message && error.error.message.indexOf(`PasswordStrengthError`) > -1) {
            result = {
              error: 'AUTH0_CREATE_WEAK_PASSWORD_FAILURE',
              code: 'AUTH0_CREATE_WEAK_PASSWORD_FAILURE',
              text: `No more than 2 identical chars in a row\n Special characters (!@#$%^&*)\n Lower case (a-z), upper case (A-Z) and numbers (0-9)\n Must have 8 characters in length\n Non-empty password required`,
            };
          } else {
            result = {
              error: 'AUTH0_CREATE_EXISTING_USER_FAILURE',
              code: 'AUTH0_CREATE_EXISTING_USER_FAILURE',
              text: error.error.message,
            };
          }
        } else {
          result = {
            error: 'AUTH0_CREATE_ACCOUNT_UNKNOWN_FAILURE',
            code: 'AUTH0_CREATE_ACCOUNT_UNKNOWN_FAILURE',
            text: `There was an issue creating your account.  Please contact support.`,
          };
        }
      }
    }

    return result;
  }

  @remote({ on: 'server' })
  async changePasswordAndLogin(): Promise<void> {
    await this.publicChangePasswordFromToken();
    await this.postAuthExecution(this.getPrincipal(), true);
  }

  protected abstract returnInformationToClient(
    identifiedPrincipal: IdentifiedPrincipal
  ): Promise<AuthenticationInformation>;

  @remote({ on: 'server' })
  protected convertPolicy(customer: PrincipalCustomer, fetchAccessories: boolean = false): Promise<void> {
    return Promise.resolve();
  }

  protected defaultSettingsRoute() {
    // NO OP
  }

  private isChangeEmailFlow(flow: string): boolean {
    let isChangeEmailFlow: boolean = true;

    if (flow && flow.indexOf('primary_identity_changed') > -1) {
      isChangeEmailFlow = false;
    }

    return isChangeEmailFlow;
  }

  @remote({ on: 'server' })
  public async newPublicLoginWithNewEmail(newEmail: string, pass: string, flow?: string): Promise<void> {
    await this.preAuthExecution();

    const identifiedPrincipal: PrincipalCustomer = await Auth0Utils.loadPrincipalForEmailChange(newEmail);

    if (!identifiedPrincipal) {
      const error = {
        isHumanRelated: false,
        data: {
          newEmail: newEmail,
        },
      };
      LoggerSingleton.getInstance().error(
        {
          module: 'Auth0Controller',
          function: 'newPublicLoginWithNewEmail',
          category: Category.security,
          context: { piiLevel: PiiLevelEnum.s2 },
          error: error,
        },
        'No email found while attempting to change email'
      );

      throw { code: 'incorrect_email_or_password', text: 'Incorrect email or password' };
    }

    await identifiedPrincipal.consumeEmailVerificationCode(this.verifyEmailCode);
    const oldEmail: string = identifiedPrincipal.email;

    if (this.isAuth0Enabled === null) {
      this.isAuth0Enabled = await Auth0Utils.isAuth0Enabled();
    }

    LoggerSingleton.getInstance().info(
      {
        module: 'Auth0Controller',
        function: 'newPublicLoginWithNewEmail',
        category: Category.security,

        data: {
          isAuth0Enabled: this.isAuth0Enabled,
        },
      },
      'Login with new email'
    );

    if (this.isAuth0Enabled) {
      const emailToAuthenticate = this.isChangeEmailFlow(flow) ? oldEmail : newEmail;

      const auth0Response: Auth0Response = await Auth0Client.authenticate(emailToAuthenticate, pass);

      if (auth0Response) {
        this.setAuth0Properties(auth0Response);

        if (this.isChangeEmailFlow(flow)) {
          await Auth0Client.updateEmail(this.auth0userId, newEmail);
        }
      } else {
        LoggerSingleton.getInstance().error(
          {
            module: 'Auth0Controller',
            function: 'newPublicLoginWithNewEmail',
            context: { piiLevel: PiiLevelEnum.s2 },
            category: Category.security,
            error: {
              isHumanRelated: false,
              data: {
                newEmail: newEmail,
              },
            },
          },
          'Email change verification failed during authentication'
        );

        throw new Error(`There was a problem changing user email`);
      }
    } else {
      if (identifiedPrincipal.mustChangePassword) {
        if (!this.newPassword) {
          throw { code: 'changePassword', text: 'Please change your password' };
        }

        await this.changePasswordForPrincipal(identifiedPrincipal);
      }
    }

    identifiedPrincipal.email = newEmail;
    identifiedPrincipal.newEmail = '';
    await identifiedPrincipal.persistSave();

    await this.sendEmail(
      'confirm_emailchange',
      newEmail,
      `${identifiedPrincipal.firstName} ${identifiedPrincipal.lastName}`,
      [
        { name: 'email', content: newEmail },
        { name: 'firstName', content: identifiedPrincipal.firstName },
      ]
    );

    await this.authenticatePrincipal(identifiedPrincipal, true, pass);
    await this.postAuthExecution(identifiedPrincipal, true);
    await this.postProcessEmailChange();
  }

  protected decodeJwtToken(token): jwt.JwtPayload {
    const decodedToken: string | jwt.JwtPayload = jwt.decode(token);

    if (typeof decodedToken === 'string' || decodedToken instanceof String) {
      throw new jwt.JsonWebTokenError('decoded token received was not the expected format');
    } else {
      return decodedToken as jwt.JwtPayload;
    }
  }

  protected async preAuthExecution(): Promise<string> {
    if (this.loggedIn) {
      UILoggerSingleton.getInstance().warn(
        {
          module: 'Auth0Controller',
          function: 'preAuthExecution',
          category: UICategory.security,
        },
        'Attempting to authenticate user already logged in'
      );

      throw { code: 'already_loggedin', text: 'Already logged in' };
    }
    return '';
  }

  // check if identity used to login is locked
  protected async postGetPrincipal() {
    if (this.authenticatedIdentity && this.authenticatedIdentity.isLocked) {
      // should log user out?
      UILoggerSingleton.getInstance().info(
        {
          module: 'Auth0Controller',
          function: 'postGetPrincipal',
          category: UICategory.security,
        },
        'Identity is locked'
      );
      throw { code: 'identity_locked', text: 'Identity locked - cannot log in' };
    }
  }

  protected async identifyPrincipal(
    email: string,
    firstName: string,
    lastName: string,
    policyNumber?: string | null,
    applicationNumber?: string | null
  ): Promise<IdentifiedPrincipal> {
    let isExistingCustomerObject: boolean = true;

    const customerAccount: Account = await this.fetchCustomerAccount();

    UILoggerSingleton.getInstance().info(
      {
        module: 'Auth0Controller',
        function: 'identifyPrincipal',
        data: {
          isAuth0Enabled: await this.getIsAuth0Enabled(),
          identifier: policyNumber || applicationNumber,
        },
        category: UICategory.security,
      },
      'Identified principal'
    );

    if (customerAccount && this.isAuth0Enabled) {
      const customer = await this.loadPrincipal({
        policyNumber,
        account: customerAccount,
        loadAccount: false,
        applicationNumber,
      });

      return {
        principal: customer as PrincipalCustomer,
        isExistingUser: isExistingCustomerObject,
      };
    }
    // -----------------------------------------
    else {
      const principals = await this.fetchPrincipalQuery(email);
      let principal;

      if (principals.length === 0) {
        if (this.isAuth0Enabled) {
          // None found -> Create a new account
          principal = await this.createUser(email, firstName, lastName);
          isExistingCustomerObject = false;
        }
      } else {
        // Match found -> Use existing account
        principal = principals[0];
      }

      // Suspended or Non-Existent
      if (!principal) {
        UILoggerSingleton.getInstance().warn(
          {
            module: 'Auth0Controller',
            function: 'identifyPrincipal',
            context: { piiLevel: UIPiiLevelEnum.s2 },
            data: {
              email: email,
              hasPrincipal: !!principal,
            },
            category: UICategory.security,
          },
          'Log In attempt failed.  Unable to identify user'
        );

        throw {
          code: 'invalid_user',
          text: 'Incorrect email or password',
        };
      }

      if (principal.suspended) {
        UILoggerSingleton.getInstance().warn(
          {
            module: 'Auth0Controller',
            function: 'identifyPrincipal',
            context: { piiLevel: UIPiiLevelEnum.s2 },
            data: {
              email: email,
              isSuspended: principal.suspended,
            },
            category: UICategory.security,
          },
          'Log In attempt failed (user suspended)'
        );

        throw {
          code: 'invalid_user_suspended',
          text: 'User Suspended',
        };
      }

      UILoggerSingleton.getInstance().info(
        {
          module: 'Auth0Controller',
          function: 'identifyPrincipal',
          context: { piiLevel: UIPiiLevelEnum.s2 },
          data: {
            identifiedPrincipal: principal.email,
            authOwner: principal.authOwner,
            isExistingUser: isExistingCustomerObject,
          },
          category: UICategory.request,
        },
        'Identified principal'
      );

      return {
        principal: principal,
        isExistingUser: isExistingCustomerObject,
      };
    }
  }

  // TODO: Move to a service
  protected async fetchCustomerAccount(): Promise<Account> {
    let customerAccount: Account = null;

    if (this.auth0userId) {
      const identity: Identity = await this.getIdentityService().loadIdentityByIdentifier(this.auth0userId);

      if (identity) {
        customerAccount = identity.account;
        this.setIdentityAndAccountControllerInfo(identity);
      }
    }

    return customerAccount;
  }

  // NAME????
  protected setIdentityAndAccountControllerInfo(identity: Identity) {
    this.authenticatedIdentity = identity;
    this.primaryIdentity = Auth0Utils.getPrimaryIdentityFromAccount(identity.account);
  }

  protected fetchPrincipalQuery(email: string): Promise<Array<PrincipalCustomer>> {
    return this.getPrincipal().amorphicClass.persistorFetchByQuery(
      queryFilter.call(this, {
        email: {
          $regex: new RegExp('^' + email.toLowerCase().replace(ALPHANUMERIC_REGEX, '\\$1') + '$'),
          $options: 'i',
        },
      })
    );
  }

  protected async authenticatePrincipal(
    principal: PrincipalCustomer,
    isExistingAccount: boolean,
    password: string
  ): Promise<void> {
    UILoggerSingleton.getInstance().info(
      {
        module: 'Auth0Controller',
        function: 'authenticatePrincipal',
        context: { piiLevel: UIPiiLevelEnum.s2 },
        data: {
          email: principal ? principal.email : null,
        },
        category: UICategory.security,
      },
      'Authenticating Principal'
    );

    if (isExistingAccount) {
      principal = await this.getPrincipal().amorphicClass.persistorFetchById(principal._id);

      if (!this.isAuth0Enabled) {
        if (AuthOwner.AUTH0 === principal.authOwner) {
          UILoggerSingleton.getInstance().info(
            {
              module: 'Auth0Controller',
              function: 'authenticatePrincipal',
              context: { piiLevel: UIPiiLevelEnum.s2 },
              data: {
                email: principal.email,
              },
              category: UICategory.security,
            },
            'Recognizing legacy auth method with an Auth0 migrated account. Authenticating with Auth0'
          );

          try {
            await Auth0Client.authenticate(principal.email, password);
            await principal.establishPassword(password, null, true, false);
          } catch (error) {
            if (error && error.error && error.error.error_description) {
              throw {
                code: error.error.error_description,
                text: error.error.error_description,
              };
            } else {
              throw {
                code: 'AUTH0_AUTHENTICATE_UNKNOWN_FAILURE',
                text: `There was an issue authenticating your account.  Please contact support.`,
              };
            }
          }
        } else {
          UILoggerSingleton.getInstance().info(
            {
              module: 'Auth0Controller',
              function: 'authenticatePrincipal',
              context: { piiLevel: UIPiiLevelEnum.s2 },
              data: {
                email: principal.email,
                isExistingAccount: isExistingAccount,
              },
              category: UICategory.security,
            },
            'Recognizing legacy auth method. Performing traditional authentication'
          );
          await principal.authenticate(password, null, null);
          this.amorphicate(principal);
        }
      } else {
        if (this.auth0IdToken) {
          this.amorphicate(principal);
        } else {
          UILoggerSingleton.getInstance().warn(
            {
              module: 'Auth0Controller',
              function: 'authenticatePrincipal',
              context: { piiLevel: UIPiiLevelEnum.s2 },
              data: {
                email: principal.email,
                isExistingAccount: isExistingAccount,
              },
              category: UICategory.security,
            },
            'Attempted to log in without Auth0 ID Token'
          );

          throw {
            code: 'invalid_authentication',
            text: 'Login attempt without valid ID Token',
          };
        }
      }
    }

    this.setLoggedInState(principal);

    UILoggerSingleton.getInstance().info(
      {
        module: 'Auth0Controller',
        function: 'authenticatePrincipal',
        context: { piiLevel: UIPiiLevelEnum.s2 },
        data: {
          email: principal ? principal.email : null,
        },
        category: UICategory.milestone,
      },
      'Successfully set the principal and login state'
    );
  }

  protected async preAuthenticatePrincipal(principal: PrincipalCustomer): Promise<void> {
    // NO OP
  }

  protected async preLoggedInState(principal: PrincipalCustomer): Promise<void> {
    // NO OP
  }

  protected async postAuthExecution(principal: PrincipalCustomer, isExistingAccount: boolean): Promise<void> {
    if (isExistingAccount) {
      // Load account data
      await this.fetchData();
      await this.cleanData();
      await this.runPostLoginActions();
    } else {
      await this.runNewAccountActions();
    }

    // Update Identity
    await this.updateIdentity(principal);

    UILoggerSingleton.getInstance().info(
      {
        module: 'Auth0Controller',
        function: 'postAuthExecution',
        category: UICategory.milestone,
        context: { piiLevel: UIPiiLevelEnum.s2 },
        data: {
          email: principal ? principal.email : null,
        },
      },
      'Successfully ran base post auth execution'
    );
  }

  // TODO: Make this take in the identifier instead of relying on its existence in the session
  protected abstract updateIdentity(principal: PrincipalCustomer): Promise<void>;

  protected abstract updateAuthOwner(authOwner: AuthOwner): Promise<void>;

  protected async fetchData(): Promise<boolean> {
    return null;
  }

  protected async cleanData(): Promise<string> {
    return null;
  }

  protected abstract runNewAccountActions(): Promise<void>;

  protected async runPostLoginActions(): Promise<void> {}

  protected async notifyMfaAccountChangeIfNeeded(): Promise<void> {
    if (await this.getIsAuth0Enabled()) {
      const accountInfo: Auth0ResponseAccount = await Auth0Client.getAccountInfo(this.email);
      const userMetaData: Auth0UserMetadata = accountInfo.user_metadata;
      if (userMetaData && userMetaData.enable_mfa && !userMetaData.mfa_email_sent) {
        this.sendEmail('mfa_enabled', this.email, this.firstName + ' ' + this.lastName, []);
        Auth0Client.setMfaEmailSent(this.auth0userId);
      }
    }
  }

  protected async createUser(email: string, firstName: string, lastName: string): Promise<PrincipalCustomer> {
    this.setPrincipal(this.getPrincipal() || this.newPrincipal());
    const principal = this.getPrincipal();
    principal.email = email;
    principal.firstName = firstName;
    principal.lastName = lastName;

    await AuthenticationHelpers.insertFilter.call(this, principal);
    await principal.persistSave();

    return principal;
  }

  @remote({ on: 'server' })
  async newChangeEmail(newEmail: string, newPassword: string): Promise<any> {
    const url = urlparser.parse(this.auth0redirectUri, true);
    const principal = this.getPrincipal();
    const oldEmail = principal.email;

    return Auth0Client.authenticate(oldEmail, newPassword)
      .then((_auth0Response: Auth0Response) => {
        return this.getPrincipal().amorphicClass.persistorCountByQuery(
          queryFilter.call(this, {
            email: {
              $regex: new RegExp('^' + newEmail.toLowerCase().replace(ALPHANUMERIC_REGEX, '\\$1') + '$'),
              $options: 'i',
            },
          })
        );
      })
      .then((count) => {
        if (count > 0) {
          throw { code: 'email_registered', text: 'This email is already registered' };
        }
      })
      .then(() => {
        if (validateEmail.call(this)) {
          return principal.setEmailVerificationCode();
        }
      })
      .then(() => {
        if (!deferEmailChange.call(this)) {
          this.email = newEmail;
        }

        principal.newEmail = newEmail;
        this.amorphicate(principal);
        principal.persistSave();

        // Send an email to old email address which is purely informational
        this.sendEmail('email_changed', oldEmail, principal.firstName + ' ' + principal.lastName, [
          { name: 'oldEmail', content: oldEmail },
          { name: 'email', content: newEmail },
          { name: 'firstName', content: principal.firstName },
        ]);

        // Send an email to new email address asking to verify the new email
        // address
        this.sendEmail(
          validateEmail.call(this) ? 'email_changed_verify' : 'email_changed',
          newEmail,
          principal.firstName + ' ' + principal.lastName,
          [
            { name: 'oldEmail', content: oldEmail },
            { name: 'email', content: newEmail },
            { name: 'firstName', content: principal.firstName },
            {
              name: 'link',
              content:
                url.protocol +
                '//' +
                url.host.replace(/:.*/, '') +
                (parseInt(url.port) > 1000 ? ':' + url.port : '') +
                '?email=' +
                encodeURIComponent(newEmail) +
                '&code=' +
                principal.validateEmailCode +
                (deferEmailChange.call(this) ? '#verify_email_change' : '#verify_email'),
            },
            { name: 'verificationCode', content: principal.validateEmailCode },
          ]
        );

        LoggerSingleton.getInstance().info(
          {
            module: 'Auth0Controller',
            function: 'newChangeEmail',
            category: Category.security,
            context: { piiLevel: PiiLevelEnum.s2 },
            data: {
              oldEmail: oldEmail,
              newEmail: newEmail,
            },
          },
          'Successfully changed email'
        );

        return true;
      })
      .then(() => this.postProcessEmailChange())
      .catch((error) => {
        const errorMessage: Auth0Error = {
          code: error.error,
          text: error.error_description,
        };
        throw errorMessage;
      });
  }

  protected abstract postProcessEmailChange(): Promise<void>;

  @remote({
    validate: function () {
      return this.validate(document.getElementById('changeEmailFields'));
    },
  })
  resendChangeEmailValidationCode(url) {
    if (this.getPrincipal().validateEmailCode) {
      return super.resendChangeEmailValidationCode(url);
    } else {
      return this.getPrincipal()
        .setEmailVerificationCode()
        .then(() => {
          return super.resendChangeEmailValidationCode(url);
        });
    }
  }

  @remote({ on: 'server' })
  public async resendAccountVerificationEmail(): Promise<void> {
    await Auth0Client.resendVerificationEmail(this.auth0userId, this.getPrincipal().email, undefined);

    log.call(this, 'Resent email validation code to ' + this.getPrincipal().email);
  }

  protected abstract inNonProductionMode(): boolean;

  @remote({ on: 'server' })
  public async setEmailVerified(): Promise<void> {
    if (this.inNonProductionMode()) {
      await Auth0Client.setEmailVerified(this.auth0userId);
      this.isAccountVerified = true;
      LoggerSingleton.getInstance().info(
        {
          module: 'Auth0Controller',
          function: 'setEmailVerified',
          context: { piiLevel: PiiLevelEnum.s2 },
          data: {
            email: this.getPrincipal().email,
          },
          category: Category.security,
        },
        'Set email to verified'
      );
    }
  }

  public async resolveAccountVerificationStatus(): Promise<void> {
    if (!this.isAccountVerified) {
      this.isAccountVerified = await this.checkIfAccountVerified();
    }
  }

  @remote({ on: 'server', public: true })
  public async resolveAuth0Data(): Promise<void> {
    await this.setAuth0Configuration();
    if (this.loggedIn) {
      await this.resolveAccountVerificationStatus();
    }
  }

  @remote({ on: 'server' })
  public async setAuth0Configuration(): Promise<void> {
    if (this.isAuth0Enabled === null) {
      this.isAuth0Enabled = await this.lookUpIsAuth0Enabled();
    }

    if (this.isAuth0Enabled === true) {
      const auth0Config: Auth0Configs = this.getAuth0Configurations();

      this.auth0clientId = auth0Config.clientId;
      this.auth0CustomDomain = auth0Config.customDomain;
      this.auth0domain = auth0Config.domain;
      this.auth0responseType = auth0Config.responseType;
      this.auth0scope = auth0Config.scope;
      this.auth0connection = auth0Config.connection;
      this.auth0audience = auth0Config.audience;
      await this.setSignUpOrigin();
    }
  }

  abstract getAuth0Configurations(): Auth0Configs;

  // Must not be remote
  abstract getAuth0Secret(): string;

  abstract exchangeAuth0CodeForToken(code: string, redirectUri: string): Promise<Auth0Response>;

  @remote({ on: 'server' })
  public async toggleZenDeskLogin(): Promise<boolean> {
    this.isZenDeskLogin = !this.isZenDeskLogin;

    return this.isZenDeskLogin;
  }

  public abstract logout();

  @remote({ on: 'server' })
  public async serverLogout(): Promise<void> {
    this.setLoggedOutState();
    await this.amorphic.expireSession();
  }

  abstract setPrincipal(principal: PrincipalCustomer);

  abstract getPrincipal(): PrincipalCustomer;

  abstract newPrincipal(): PrincipalCustomer;

  protected async setSignUpOrigin(): Promise<void> {
    this.signUpOrigin = '';
  }

  public isAuth0Error(route: ActivatedRoute): boolean {
    const errorVariables = Auth0Utils.getAuth0ErrorFromUrl(route);
    if (errorVariables) {
      this.auth0errorMessage = {
        code: errorVariables.code,
        text: errorVariables.text,
      };
      this.auth0mode = errorVariables.mode;
      this.auth0ErrorFromProvider = errorVariables.provider;
      return true;
    }
    return false;
  }

  abstract publicSave();

  protected getIdentityService(): IdentityService {
    if (!this.identityService) {
      this.identityService = IdentityService.getService();
    }

    return this.identityService;
  }

  protected setIdentityService(identityService: IdentityService): void {
    this.identityService = identityService;
  }

  public getHasNoTraditionalIdentity(): boolean {
    let hasNoTraditionalIdentity = true;

    if (this.authenticatedIdentity) {
      this.authenticatedIdentity.account.activeIdentities.forEach((identity) => {
        if (identity.provider === IdentityProviders.AUTH0) {
          hasNoTraditionalIdentity = false;
        }
      });
    }

    return hasNoTraditionalIdentity;
  }

  public getIdentityProviderDisplayName(provider: string): string {
    return IdentityProvidersDisplayName[provider];
  }

  @remote({ on: 'server' })
  public async invalidateIdentity(identity: Identity): Promise<void> {
    try {
      const txn = this.amorphic.begin();
      LoggerSingleton.getInstance().info(
        {
          module: 'Auth0Controller',
          function: 'invalidateIdentity',
          category: Category.security,

          data: {
            identityId: identity._id,
          },
        },
        'Lets go to identity service to go through the invalidation flow'
      );

      await this.getIdentityService().invalidateIdentity(identity._id, identity.account, txn);

      await this.amorphic.end(txn);
    } catch (e) {
      const err = {
        name: e?.name,
        message: e?.message,
        stack: e?.stack,
        data: {
          identityId: identity._id,
        },
      };
      LoggerSingleton.getInstance().error(
        {
          module: 'Auth0Controller',
          function: 'invalidateIdentity',
          category: Category.security,
          error: err,
        },
        'There was a problem invalidating the identity'
      );

      throw { code: 'invalidate_identity', text: 'There was a problem invalidating the identity' };
    }
  }

  @remote({ on: 'server' })
  public async postInvalidatingSocialIdentity(invalidatingIdentity: Identity) {
    await this.updateAuthenticatedIdentity(invalidatingIdentity);
    await this.updatePrimaryIdentity(invalidatingIdentity);
  }

  public async updateAuthenticatedIdentity(invalidatingIdentity: Identity) {
    // if we unlinked the identity we logged in with, set a new authenticated identity
    // this can only happen for a case where social is the primary identity
    // const isSocialAccount = this.getHasNoTraditionalIdentity();
    const hasMultipleActiveIdentities = this.authenticatedIdentity.account.activeIdentities.length > 1;

    const isInvalidatingAuthenticatedIdentity = this.authenticatedIdentity._id === invalidatingIdentity._id;
    if (isInvalidatingAuthenticatedIdentity && hasMultipleActiveIdentities) {
      this.authenticatedIdentity = this.authenticatedIdentity.account.activeIdentities.find(
        (identity) => identity._id !== invalidatingIdentity._id
      );
    }
  }

  public async updatePrimaryIdentity(invalidatingIdentity: Identity) {
    // if we unlinked the primary identity, update controllers primaryIdentity to reflect it
    if (this.primaryIdentity._id === invalidatingIdentity._id) {
      this.primaryIdentity = this.authenticatedIdentity.account.activeIdentities.find((identity) => identity.isPrimary);
    }
  }

  public async updatePrincipaEmail() {
    // new primary can mean new primary email address for social cases
    const principal = this.getPrincipal();

    const isSocialAccount = this.getHasNoTraditionalIdentity();

    // in case of social primary
    // they can switch from one to the other
    // so we need to _update_ the email to the one of the last active identity
    if (isSocialAccount) {
      const auth0User = await IdentityService.getService().getAuth0User(this.primaryIdentity);

      principal.email = auth0User.email;

      await principal.persistSave();
    }
  }

  /**
   * We are validating if the session still exists
   * Exchange code for token, and parse the info
   * Create a new identity with the parsed identifier
   * Link the identity to the customers Account
   *
   * @param auth0Result
   * @param fragment
   */
  @remote({ on: 'server' })
  public async updateSession(auth0Result: Auth0AuthorizeResponse, fragment: string): Promise<void> {
    if (!this.isLoggedIn || !this.email) {
      throw { code: 'identity_linking_error', text: 'There was a problem linking identities' };
    }

    await this.toggleAccountLinkingPendingStatus(false);

    const customerAccount = this.authenticatedIdentity.account;
    // create identity from auth0 user data
    const identifier = await this.parseIdentifierFromAuth0Response(auth0Result, fragment);
    await IdentityService.getService().createNewIdentityForAccount(
      identifier,
      customerAccount,
      false,
      IdentitySource.MANUAL_LINK
    );

    // link the identity to the auth0 primary account if this is not the primary one
    const primaryIdentity = Auth0Utils.getPrimaryIdentityFromAccount(customerAccount);
    if (primaryIdentity.identifier !== identifier) {
      await Auth0Client.linkAuth0User(primaryIdentity.identifier, identifier);
    }
  }

  @remote({ on: 'server' })
  private async parseIdentifierFromAuth0Response(
    auth0Result: Auth0AuthorizeResponse,
    fragment: string
  ): Promise<string> {
    const paramMap = Auth0Utils.parseAuthenticationFragment(fragment);

    const token: Auth0Response = await this.exchangeAuth0CodeForToken(paramMap.code, this.auth0redirectUri);

    const tokenPayload: any = this.decodeJwtToken(token.id_token);
    const identifier = tokenPayload.sub;

    const auth0Issuer: string = await Auth0Client.getWellKnownConfigIssuer(this.auth0domain);
    // verifying with the well-known oidc issuer spec

    if (tokenPayload.iss !== auth0Issuer) {
      LoggerSingleton.getInstance().error(
        {
          module: 'Auth0Controller',
          function: 'processAuth0IdentityCode',
          category: Category.security,
          context: { piiLevel: PiiLevelEnum.nonPII },
          data: {
            identifier,
            issuers: {
              token: tokenPayload.iat,
              wellknown: this.auth0domain,
            },
          },
        },
        'Processed Auth0 identity code'
      );
      throw new Error('issuer on token does not match well known configuration for domain');
    }

    LoggerSingleton.getInstance().info(
      {
        module: 'Auth0Controller',
        function: 'processAuth0IdentityCode',
        category: Category.security,
        context: { piiLevel: PiiLevelEnum.nonPII },
        data: {
          identifier,
        },
      },
      'Processed Auth0 identity code'
    );

    return identifier;
  }

  /**
   * Create a traidtional (email/password) identity flow
   * Create an unverified auth0 user with the new email
   * Link that user in auth0
   * Mark other identites isPrimary=false if they exist
   * Create a new identity with the new auth0 user for customer account and mark it isPrimary
   * Save the email/password to the principal
   *
   * @param email
   * @param password
   */
  @remote({ on: 'server' })
  public async createAndLinkTraditionalIdentityAsNewPrimary(
    email: string,
    password: string,
    url: string
  ): Promise<void> {
    LoggerSingleton.getInstance().info(
      {
        module: 'Auth0Controller',
        function: 'createAndLinkTraditionalIdentityAsNewPrimary',
        category: Category.milestone,
        context: { piiLevel: PiiLevelEnum.s2 },
        data: {
          email: email,
        },
      },
      'Create a traditional identity to be the new primary for the account'
    );

    try {
      // create an auth0 user with unverified email
      const auth0User = await Auth0Client.createVerifiedAccount(email, password);

      // create new identity for our account object
      const newIdentity = await IdentityService.getService().createNewIdentityForAccount(
        auth0User.user_id,
        this.authenticatedIdentity.account,
        true,
        IdentitySource.MANUAL_LINK
      );

      // this is now the primary one
      this.authenticatedIdentity = newIdentity;
      this.primaryIdentity = newIdentity;
    } catch (err) {
      // update account, we are not pending anymore
      LoggerSingleton.getInstance().error(
        {
          module: 'Auth0Controller',
          function: 'createAndLinkTraditionalIdentityAsNewPrimary',
          category: Category.security,
          context: { piiLevel: PiiLevelEnum.s2 },
          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
            data: {
              email: email,
              identity: this.authenticatedIdentity._id,
            },
          },
        },
        'Error creating new tradiational identity as primary'
      );

      throw err;
    }
  }

  protected async verifyCustomerIdentityMatchesAuth0Id(customer): Promise<void> {
    if (customer && customer._id) {
      const account: Account = await IdentityService.getService().getCustomerAccount(customer);
      let authenticatedIdentityMatchesActiveIentity: boolean = false;
      if (account && account.identities) {
        let activeIdentities: number = 0;

        account.identities.forEach((identity: Identity) => {
          if (identity.isActive) {
            activeIdentities++;

            if (identity.identifier === this.auth0userId) {
              authenticatedIdentityMatchesActiveIentity = true;
            }
          }
        });

        if (!authenticatedIdentityMatchesActiveIentity && activeIdentities > 0) {
          UILoggerSingleton.getInstance().error(
            {
              module: 'Auth0Controller',
              function: 'verifyCustomerIdentityMatchesAuth0Id',
              category: UICategory.security,
              error: {
                isHumanRelated: false,
                data: {
                  customerId: customer ? customer._id : null,
                  auth0Id: this.auth0userId,
                },
              },
            },
            'Authenticated Identity does not match registered Identity'
          );
          throw { code: 'identity_mismatch', text: 'Authenticated Identity does not match registered Identity' };
        }
      }
    }
  }

  public async sendEmailToConfirmAccountChange(
    newEmail: string,
    otherUrl: string,
    principal?: PrincipalCustomer
  ): Promise<void> {
    const url = urlparser.parse(otherUrl, true);

    let thisPrincipal = principal;

    if (thisPrincipal !== null) {
      const identifiedPrincipal = await this.identifyPrincipal(this.email, null, null, null, null);
      thisPrincipal = identifiedPrincipal.principal;
    }

    thisPrincipal.setEmailVerificationCode();

    const linkContent =
      url.protocol +
      '//' +
      url.host.replace(/:.*/, '') +
      (parseInt(url.port) > 1000 ? ':' + url.port : '') +
      '?email=' +
      encodeURIComponent(newEmail) +
      '&code=' +
      thisPrincipal.validateEmailCode +
      '#primary_identity_changed';

    try {
      await this.sendEmail(
        'primary_identity_changed',
        newEmail,
        thisPrincipal.firstName + ' ' + thisPrincipal.lastName,
        [{ name: 'link', content: linkContent }]
      );
    } catch (err) {
      UILoggerSingleton.getInstance().error(
        {
          module: 'Auth0Controller',
          function: 'sendEmailToConfirmAccountChange',
          category: UICategory.security,
          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
          },
        },
        'Errored out'
      );
    }
  }

  public async persistEmailAndPasswordToPrincipal(email: string, password: string, url: string): Promise<void> {
    const principal: AuthenticatedPrincipal = await this.getPrincipal().amorphicClass.persistorFetchById(
      this.getPrincipal()._id
    );

    principal.newEmail = email;

    const expires = passwordExpiresMinutes.call(this)
      ? new Date(new Date().getTime() + passwordExpiresMinutes.call(this) * 1000 * 60)
      : null;

    await principal.establishPassword(password, expires, false, true);
    await principal.persistSave();

    await this.sendEmailToConfirmAccountChange(email, url, principal);
  }

  public async resendEmailToConfirmAccountChange(email: string, url: string) {
    this.sendEmailToConfirmAccountChange(email, url);
  }

  @remote({ on: 'server' })
  public async toggleAccountLinkingPendingStatus(linkingPending: boolean) {
    const customerAccount = this.authenticatedIdentity.account;

    try {
      LoggerSingleton.getInstance().info(
        {
          module: 'Auth0Controller',
          function: 'toggleAccountLinkingPendingStatus',
          category: Category.security,

          data: {
            'account.linkingPending': linkingPending,
            'account': customerAccount._id,
          },
        },
        'Toggling linkingPending on account'
      );

      customerAccount.linkingPending = linkingPending;
      await customerAccount.persistSave();
    } catch (err) {
      LoggerSingleton.getInstance().error(
        {
          module: 'Auth0Controller',
          function: 'toggleAccountLinkingPendingStatus',
          category: Category.security,
          context: { piiLevel: PiiLevelEnum.nonPII },
          error: {
            name: err?.name,
            message: err?.message,
            stack: err?.stack,
            isHumanRelated: false,
            data: {
              'account.linkingPending': linkingPending,
              'account': customerAccount._id,
            },
          },
        },
        'Yikes there was an error toggling linkingPending for the account'
      );
      throw err;
    }
  }

  @remote({ on: 'server' })
  public async lockIdentity(identityToLock: Identity) {
    await IdentityService.getService().lockIdentity(identityToLock);
  }

  @remote({ on: 'server' })
  public async unlockIdentity(identityToLock: Identity) {
    await IdentityService.getService().unlockIdentity(identityToLock);
  }

  @remote({ on: 'server' })
  public async reactivateIdentity(identityToReactivate: Identity) {
    // const txn = this.amorphic.begin();

    await IdentityService.getService().reactivateIdentity(identityToReactivate);
    // await this.amorphic.end(txn);
  }

  public async setRedirectUri(uri: string): Promise<void> {
    this.auth0redirectUri = uri;
  }

  requiresEligibility(customer: Customer): boolean {
    return !(customer.primaryCustomer && customer.primaryCustomer.address && customer.primaryCustomer.address.state);
  }

  requiresApplyForm(customer: Customer): boolean {
    return !(
      customer.application &&
      customer.application.primaryPolicy &&
      customer.application.primaryPolicy.submittedAt
    );
  }

  @remote({ on: 'server' })
  public async getPrimaryIdentityForCustomer(customer: ManagedEntity): Promise<void> {
    LoggerSingleton.getInstance().info(
      {
        module: 'Auth0Controller',
        function: 'getPrimaryIdentityForCustomer',

        category: Category.security,
      },
      'Getting identity for customer'
    );

    const identity: Identity = await this.getIdentityService().getPrimaryIdentity(customer);

    if (identity) {
      this.hasAMCidentity = true;
      LoggerSingleton.getInstance().info(
        {
          module: 'Auth0Controller',
          function: 'getPrimaryIdentityForCustomer',
          category: Category.security,

          data: {
            identity: identity._id,
          },
        },
        'Snagged identity for customer'
      );
    } else {
      LoggerSingleton.getInstance().info(
        {
          module: 'Auth0Controller',
          function: 'getPrimaryIdentityForCustomer',

          category: Category.security,
        },
        'Did not snag identity for customer'
      );
    }
  }

  @remote({ on: 'server' })
  public async createTraditionalIdentity(newEmail: string, password: string, url: string): Promise<void> {
    try {
      await this.validateNewEmail(newEmail);

      // mark account as pending linking of this identity
      await this.toggleAccountLinkingPendingStatus(true);

      await this.createAndLinkTraditionalIdentityAsNewPrimary(newEmail, password, url);

      // save the new email/password combo to the principal object
      // change email/password on customer object?
      await this.persistEmailAndPasswordToPrincipal(newEmail, password, url);
    } catch (identityCreationError) {
      try {
        // do not lock or invalidate last social identity if we ran into an error
        await this.toggleAccountLinkingPendingStatus(false);
      } catch (recoveryError) {
        LoggerSingleton.getInstance().error(
          {
            module: 'Auth0Controller',
            function: 'createTraditionalIdentity',
            category: Category.security,
            context: { piiLevel: PiiLevelEnum.s2 },
            error: {
              name: recoveryError?.name,
              message: recoveryError?.message,
              stack: recoveryError?.stack,
              isHumanRelated: false,
              data: {
                newEmail: newEmail,
                authenticatedIdentity: this.authenticatedIdentity ? this.authenticatedIdentity._id : null,
              },
            },
          },
          'Unable to recover primary identity'
        );
      }

      if (identityCreationError.text) {
        throw { code: identityCreationError.code, text: identityCreationError.text };
      } else {
        throw { code: 'create_traditional_identity_flow', text: 'There was a problem creating the identity' };
      }
    }
  }

  private async validateNewEmail(newEmail: string): Promise<void> {
    const emailTaken: boolean = await CustomerUtils.customerWithEmailExists(newEmail);

    if (emailTaken) {
      throw { code: 'email_taken', text: 'Email address is not available' };
    }
  }

  public getAuth0LoginHint(): string {
    return null;
  }

  @remote({ on: 'server' })
  public async checkAndSetAccountVerified(): Promise<boolean> {
    if (!this.isAccountVerified) {
      try {
        this.isAccountVerified = await this.checkIfAccountVerified();
      } catch (error) {
        return false;
      }
    }
    return this.isAccountVerified;
  }

  public async loadPrincipal({
    policyNumber,
    account,
    loadAccount,
    applicationNumber,
  }: {
    policyNumber?: string | null;
    account?: Account;
    loadAccount: boolean;
    applicationNumber?: string | null;
  }): Promise<PrincipalCustomer | void> {
    if (loadAccount && !account) {
      account = await this.fetchCustomerAccount();
    }

    if (!account || !this.isAuth0Enabled) {
      return;
    }

    let customer: PrincipalCustomer;
    const accountAuthorizations = account.accountAuthorizations;
    const customerIds: Array<string> = accountAuthorizations.map((accountAuth) => accountAuth.managedEntityId);

    if (!applicationNumber) {
      UILoggerSingleton.getInstance().info(
        {
          module: 'Auth0Controller',
          function: 'loadPrincipal',
          data: {
            auth0userId: this.auth0userId,
          },
          category: UICategory.security,
        },
        'There is no applicationNumber passed in as a query string, pulling the first Principal object'
      );
      if (customerIds[0]) {
        customer = await Customer.persistorFetchById(customerIds[0], { session: this.amorphic });
      }
    } else {
      try {
        const customersToLoad = await Application.getKnex()
          .select('application.customer_id')
          .where('application.customer_id', 'in', customerIds)
          .andWhere('application.applicationNumber', '=', `${applicationNumber}`);

        if (customersToLoad.length === 1) {
          customer = await Customer.persistorFetchById(customersToLoad[0].customer_id, { session: this.amorphic });
        } else {
          const message = !customersToLoad.length
            ? 'Found no principal objects to load, loading first principal'
            : 'Found too many principal objects to load, loading first principal';
          UILoggerSingleton.getInstance().error(
            {
              module: 'Auth0Controller',
              function: 'loadPrincipal',
              category: UICategory.security,
              error: {
                isHumanRelated: false,
                data: {
                  policyNumber,
                  applicationNumber,
                },
              },
            },
            message
          );
          if (customerIds[0]) {
            customer = await Customer.persistorFetchById(customerIds[0], { session: this.amorphic });
          }
        }
      } catch (err) {
        UILoggerSingleton.getInstance().error(
          {
            module: 'Auth0Controller',
            function: 'loadPrincipal',
            category: UICategory.security,
            error: {
              name: err?.name,
              message: err?.message,
              stack: err?.stack,
              isHumanRelated: false,
              data: {
                policyNumber,
                applicationNumber,
              },
            },
          },
          'A problem occurred while getting all the account related applications from the DB'
        );
        if (customerIds[0]) {
          customer = await Customer.persistorFetchById(customerIds[0], { session: this.amorphic });
        }
      }
    }

    return customer;
  }
}

export interface IdentifiedPrincipal {
  principal: PrincipalCustomer;
  isExistingUser: boolean;
}

export interface AuthenticationInformation {
  customerId: string;
  isExistingUser: boolean;

  /**
   * Will be populated when the user fails authentication and needs to be redirected
   */
  redirectUrl?: string;

  /**
   * Redirects need to occur before registering marketing events; pre-auth has failed
   */
  skipMarketingEvents?: boolean;
}
